import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { AlertTriangle } from "lucide-react";
import { ContextualHelp } from "@shared/ui/contextual-help";
import { ScrollArea } from "@shared/ui/scroll-area";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useAvabilitySettings,
  useNotificationTemplate,
  usePriorityLevel,
  useServiceCategoryByUtility,
  useServiceSubCategoryByCategory,
  useUpdateServiceConfig,
  useUtilityServices,
} from "@features/cx/settings/hooks";
import { useCreateServiceConfig } from "@features/cx/settings/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useBaseComponent } from "@shared/base/hooks/useBaseComponent";
import { ServiceDetailFormProps } from "@features/cx/settings/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export function ServiceDetailForm({
  service,
  open,
  onClose,
  onSave,
}: ServiceDetailFormProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const [formData, setFormData] = useState({
    name: service?.name,
    code: service?.code,
    description: service?.description,
    utility_service: service?.productCode,
    category: service?.supportRequestType,
    subcategory: service?.supportRequestSubtype,
    priority: service?.extraData?.priorityLevel,
    responseTime: service?.extraData?.responseTime,
    acknowledgmentTime: service?.extraData.acknowlegmentTime || "",
    resolutionTime: service?.extraData?.resolutionTime,
    serviceFee: service?.extraData?.serviceCharge,
    fieldWorkRequired: service?.extraData?.fieldForceRequired,
    customerNotification: service?.extraData?.consumerNotification,
    businessHours: service?.extraData?.businessHours,
    availability: service?.extraData?.availability,
    requiresApproval: service?.extraData?.requiresApproval,
  });

  // Mock categories check
  const categoriesExist = true;
  const subcategoriesExist = true;

  // Data hooks
  const { data: utilities } = useUtilityServices();
  const { data: priorityData } = usePriorityLevel();
  const { data: templateData } = useNotificationTemplate();
  const { data: availabilityData } = useAvabilitySettings();

  // State for dependent dropdowns
  const [utilityService, setUtilityService] = useState<any>("");
  const [codeList, setCodeList] = useState<any>("");
  const [selectedUtility, setSelectedUtility] = useState(
    service?.productCode || ""
  );

  // Dependent data hooks
  const {
    data: serviceCategories = [],
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useServiceCategoryByUtility(utilityService);
  const { data: subServiceCategories = [], refetch: refetchSubCategories } =
    useServiceSubCategoryByCategory(codeList);

  // Hook for creating service config
  const createServiceConfig = useCreateServiceConfig();
  const updateConfig = useUpdateServiceConfig(service?.id);
  useEffect(() => {
    if (open) {
      refetchCategories();
      refetchSubCategories();
    }
  }, [open, refetchCategories, refetchSubCategories]);

  useEffect(() => {
    const operation = service ? "EDIT" : "CREATE";

    switch (operation) {
      case "EDIT":
        setFormData({
          name: service.name || "",
          code: service.configurationCode || "",
          description: service.longDescription || "",
          utility_service: service.productCode || [],
          category: service.supportRequestType || "",
          subcategory: service.supportRequestSubtype || "",
          priority: service.extraData?.priorityLevel?.toString() || "",
          responseTime: service.extraData?.responseTime || "",
          acknowledgmentTime: service.extraData?.acknowlegmentTime || "",
          resolutionTime: service.extraData?.resolutionTime || "",
          serviceFee: service.extraData?.serviceCharge || "",
          fieldWorkRequired: service.extraData?.fieldForceRequired || false,
          customerNotification:
            service.extraData?.consumerNotification?.toString() || "",
          businessHours: service.extraData?.businessHours || "",
          availability: service.extraData?.availability?.toString() || "",
          requiresApproval: service.extraData?.requiresApproval || false,
        });
        setSelectedUtility(service.productCode || "");
        // Also set the dependent dropdown states for edit mode
        setUtilityService(service.productCode || "");
        setCodeList(service.supportRequestType || "");
        break;
    }
  }, [service]);

  // Memoized options
  const getUtilityOptions = useMemo(() => {
    if (!utilities || !Array.isArray(utilities)) {
      return [];
    }
    return utilities
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilities]);

  const getPriorityOptions = useMemo(() => {
    if (!priorityData || !Array.isArray(priorityData)) {
      return [];
    }
    return priorityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [priorityData]);

  const getTemplateOptions = useMemo(() => {
    if (!templateData || !Array.isArray(templateData)) {
      return [];
    }
    return templateData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [templateData]);

  const getAvabilibiltyOptions = useMemo(() => {
    if (!availabilityData || !Array.isArray(availabilityData)) {
      return [];
    }
    return availabilityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [availabilityData]);

  const getServiceCategoryOptions = useMemo(() => {
    if (!serviceCategories || !Array.isArray(serviceCategories)) {
      return [];
    }
    return serviceCategories.map((category) => {
      return {
        value: category.code,
        label: category.name,
      };
    });
  }, [serviceCategories]);

  const getSubServiceCategoryOptions = useMemo(() => {
    if (!subServiceCategories || !Array.isArray(subServiceCategories)) {
      return [];
    }
    return subServiceCategories.map((subCategory) => {
      return {
        value: subCategory.code,
        label: subCategory.name,
      };
    });
  }, [subServiceCategories]);

  const formFields: FormField[] = [
    // Basic Service Information Group
    {
      name: "name",
      type: "text",
      label: "Service Name",
      placeholder: "e.g., New Water Connection",
      required: true,
      disabled: !!service,
      group: "Basic Service Information",
      className: "col-span-1",
    },
    {
      name: "code",
      type: "text",
      label: "Service Code",
      placeholder: "e.g., WC-NEW-001",
      disabled: !!service,
      required: true,
      group: "Basic Service Information",
      className: "col-span-1",
    },
    {
      name: "description",
      type: "textarea",
      label: "Service Description",
      placeholder:
        "Detailed service description including process, requirements, and outcomes",
      rows: 3,
      group: "Basic Service Information",
      className: "col-span-2",
    },

    // Service Classification Group
    {
      name: "utility_service",
      label: "Utility Services",
      type: "multi-select",
      required: true,
      options: getUtilityOptions,
      placeholder: "Select utility services...",
      fullWidth: true,
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      group: "Service Classification",
      className: "col-span-2",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: `w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200`,
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "category",
      type: "select",
      label: "Service Category",
      placeholder: "Select category",
      required: true,
      group: "Service Classification",
      options: getServiceCategoryOptions,
      className: "col-span-1",
    },
    {
      name: "subcategory",
      type: "select",
      label: "Service Subcategory",
      placeholder: "Select subcategory",
      required: true,
      group: "Service Classification",
      options: getSubServiceCategoryOptions,
      className: "col-span-1",
    },

    // Service Configuration Group
    {
      name: "priority",
      type: "select",
      label: "Priority Level",
      placeholder: "Select priority",
      required: true,
      group: "Service Configuration",
      options: getPriorityOptions,
      className: "col-span-1",
    },
    {
      name: "serviceFee",
      type: "number",
      label: "Service Fee",
      placeholder: "Enter fee amount",
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
    },
    {
      name: "acknowledgmentTime",
      type: "number",
      label: "Acknowledgment Time (hours)",
      placeholder: "Enter Acknowledgment Time",
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (hours)",
      placeholder: "Enter response time in hours",
      required: false,
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (hours)",
      placeholder: "Enter resolution time in hours",
      required: false,
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
    },

    // Workflow Configuration Group
    {
      name: "fieldWorkRequired",
      type: "switch",
      label: "Field Work Required",
      group: "Workflow Configuration",
      className: "col-span-2",
    },
    {
      name: "customerNotification",
      type: "select",
      label: "Customer Notification",
      placeholder: "Select notification method",
      required: true,
      group: "Workflow Configuration",
      options: getTemplateOptions,
      className: "col-span-2",
    },
    {
      name: "availability",
      type: "select",
      label: "Availability Settings",
      placeholder: "Select availability",
      required: true,
      group: "Workflow Configuration",
      options: getAvabilibiltyOptions,
      className: "col-span-2",
    },
    {
      name: "requiresApproval",
      type: "switch",
      label: "Requires Approval",
      group: "Workflow Configuration",
      helperText: "Whether this reason requires manager approval",
      className: "col-span-2 mt-3",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    switch (fieldName) {
      case "utility_service":
        console.log("value", value);
        setUtilityService(value);
        break;
      case "category":
        console.log("value", value);
        setCodeList(value);
        break;
      case "fieldWorkRequired":
        break;
    }
  };

  const handleSubmit = () => {
    if (formData.responseTime && formData.resolutionTime) {
      const responseTime = parseInt(formData.responseTime);
      const resolutionTime = parseInt(formData.resolutionTime);

      if (responseTime >= resolutionTime) {
        toast({
          title: "Validation Error",
          description: "Response time must be less than resolution time",
          variant: "destructive",
        });
        return;
      }
    }

    const operation = service ? "UPDATE" : "ADD";

    switch (operation) {
      case "UPDATE": {
        const updatePayload = {
          remote_utility_id: Number(remoteUtilityId),
          long_description: formData.description,
          product_code: formData.utility_service,
          support_request_type: formData.category,
          support_request_subtype: formData.subcategory,
          request_type: "Service",
          extra_data: {
            priority_level: parseInt(formData.priority),
            service_charge: formData.serviceFee,
            response_time: formData.responseTime,

            acknowlegment_time: formData.acknowledgmentTime,

            resolution_time: parseInt(formData.resolutionTime),
            field_force_required: formData.fieldWorkRequired,
            consumer_notification: parseInt(formData.customerNotification),
            availability: parseInt(formData.availability),
            requires_approval: formData.requiresApproval,
          },
        };

        updateConfig.mutateAsync(updatePayload);
        onClose();
        handleReset();
        break;
      }
      case "ADD": {
        const payload = {
          remote_utility_id: Number(remoteUtilityId),
          name: formData.name,
          configuration_code: formData.code,
          long_description: formData.description,
          product_code: formData.utility_service,
          support_request_type: formData.category,
          support_request_subtype: formData.subcategory,
          request_type: "Service",
          extra_data: {
            priority_level: parseInt(formData.priority),
            service_charge: formData.serviceFee,

            acknowlegment_time: formData.acknowledgmentTime,

            response_time: formData.responseTime,
            resolution_time: formData.resolutionTime,
            field_force_required: formData.fieldWorkRequired,
            consumer_notification: parseInt(formData.customerNotification),
            availability: parseInt(formData.availability),
            requires_approval: formData.requiresApproval,
          },
        };

        createServiceConfig.mutateAsync(payload);
        onClose();
        handleReset();
        break;
      }
    }
  };

  const handleReset = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {service ? "Edit Service" : "Create New Service"}
            <ContextualHelp
              title="Service Configuration"
              content="Configure all aspects of a service including classification, SLA requirements, workflow settings, and availability options. Services are the building blocks of your customer service offerings."
            />
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-1">
          <div className="space-y-6 pr-4">
            {/* Prerequisites Check */}
            {(!categoriesExist || !subcategoriesExist) && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-orange-800">
                        Missing Categories Alert
                      </h4>
                      <p className="text-sm text-orange-700 mt-1">
                        Some required categories or subcategories are missing.
                        Please create them first before configuring services.
                      </p>
                      <Button variant="outline" size="sm" className="mt-2">
                        Create Missing Categories
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading indicator for categories */}


            {/* Dynamic Form with Grid Layout */}
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                submitButtonText: service ? "Update Service" : "Create Service",
                resetOnSubmit: false,
                resetButtonText: "Cancel",
                spacing: "comfortable",
                classes: {
                  button: "px-6 py-2 rounded-md font-medium transition-colors",
                  submitButton:
                    "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
                  resetButton:
                    "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-sm text-gray-500 mt-1 flex",
                  checkbox:
                    "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500",
                },
              }}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
